import { useSyncExternalStore } from 'react'

import { DEFAULT_THEME } from './branding'

/**
 * Which theme the franchise is on, read from `<html data-theme>`.
 *
 * `BrandingApplier` writes that attribute the moment `central/branding` answers, and `themes.css`
 * selects on it. Most of what a theme changes is palette, and CSS handles that alone — but two of the
 * three change the header's **structure**, and a stylesheet cannot turn a left-aligned wordmark into a
 * centred one with a utility bar above it.
 *
 * So this exists for the components that genuinely differ, and for nothing else. A component that only
 * needs different colours must not read it: that is a variant nobody can restyle without editing TSX.
 *
 * `classic` when the attribute is absent, which is what `classic` means — the default palette lives in
 * `:root` and `BrandingApplier` *removes* the attribute for it rather than setting it.
 *
 * **`useSyncExternalStore`, not `useState` + an effect.** The attribute is exactly what that hook is
 * for: a mutable value outside React that the component has to stay in step with. Reading it in an
 * effect and calling `setState` would render once with the wrong theme and again with the right one —
 * the cascading shape the shell's own lint rule refuses, and it caught this.
 *
 * The subscription is not decoration. Branding arrives after the first paint and is re-fetched when the
 * tab regains focus, so a super-admin changing a franchise's theme in another tab changes it here
 * without a reload — the behaviour `BrandingApplier`'s `refetchOnWindowFocus` exists for, which would
 * otherwise stop at the stylesheet.
 */
export type ThemeName = 'classic' | 'modern-minimal' | 'cozy-bistro' | (string & {})

function subscribe(onChange: () => void): () => void {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return () => undefined
  const observer = new MutationObserver(onChange)
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
  return () => observer.disconnect()
}

function currentTheme(): ThemeName {
  if (typeof document === 'undefined') return DEFAULT_THEME
  const value = document.documentElement.getAttribute('data-theme')
  return value !== null && value.trim() !== '' ? value.trim() : DEFAULT_THEME
}

export function useThemeName(): ThemeName {
  // The third argument is the server snapshot; there is no server render here, and `classic` is the
  // honest answer for a document that does not exist yet.
  return useSyncExternalStore(subscribe, currentTheme, () => DEFAULT_THEME)
}
